import React, { useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAtom, useSetAtom } from 'jotai';
import { currentUserAtom } from '../../auth/AuthAtoms';
import { userItemsAtom } from '../../atoms/itemAtoms';
import GLOBALS from '../../config/globals';

import Modal from '../../components/Modal';
import SubPlayerPage from './SubPlayerPage';
import MidEventPage from './MidEventPage';
import StealEventPage from './StealEventPage';
import PostGameRewardsModal from '../../components/PostGameRewardsModal';
import LoadingSpinner from '../../components/LoadingSpinner';
import DefaultGameLayout from '../../components/DefaultGameLayout';
import StadiumGameLayout from '../../components/StadiumGameLayout';

import useGameEngine from '../../hooks/useGameEngine';
import useAutoManager from '../../hooks/useAutoManager';
import usePitcherFatigue from '../../hooks/usePitcherFatigue';

// ── Game speed helper ──

const getGameSpeedValue = userGameSpeed => {
    const entry = Object.values(GLOBALS.GAME_SPEED).find(
        speed => speed.name?.toLowerCase() === userGameSpeed?.toLowerCase()
    );
    return entry ? entry.value : GLOBALS.GAME_SPEED.NORMAL.value;
};

// ─────────────────────────────────────────────────────────────────────────────

const PlayGamesPage = () => {
    // ── 1. Atoms & params ──
    const [currentUser] = useAtom(currentUserAtom);
    const setUserItems = useSetAtom(userItemsAtom);
    const gameSpeed = getGameSpeedValue(currentUser.gameSpeed);
    const { gameID } = useParams();

    // ── 2. Local UI state ──
    const [autoPlay, setAutoPlay] = useState(false);
    const [managerMode, setManagerMode] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isManagerOpen, setIsManagerOpen] = useState(false);
    const [isParticipantsOpen, setIsParticipantsOpen] = useState(false);
    const [gameTheme] = useState(() => {
        const themes = ['day', 'golden', 'night'];
        return themes[Math.floor(Math.random() * themes.length)];
    });

    const stopAuto = useCallback(() => {
        setAutoPlay(false);
        setManagerMode(null);
    }, []);

    // ── 3. Hooks (no circular deps — all receive primitives) ──
    const engine = useGameEngine({ gameID, currentUser, gameSpeed, managerMode, stopAuto });

    const pitcherFatigue = usePitcherFatigue({
        game: engine.game,
        currentUser,
        currentPitcherCard: engine.currentPitcherCard,
        managerMode,
        setAutoPlay,
    });

    // ── 4. Post-reveal callback (rewards + fatigue check) ──
    const handleAfterReveal = useCallback((updatedGame) => {
        if (updatedGame.isFinal && updatedGame.rewardsAwarded && !engine.justFinalized.current) {
            engine.justFinalized.current = true;
            engine.setRewardsAwarded(updatedGame.rewardsAwarded);
            engine.setShowRewardsModal(true);

            const gameDrinks = updatedGame.rewardsAwarded.game?.energyDrinks ?? 0;
            const campaignDrinks = updatedGame.rewardsAwarded.campaign?.reward?.energyDrinks ?? 0;
            const totalDrinks = gameDrinks + campaignDrinks;
            if (totalDrinks > 0) {
                setUserItems(prev => ({
                    ...prev,
                    energy_drink: (prev.energy_drink || 0) + totalDrinks,
                }));
            }
        }
        pitcherFatigue.checkPitcherFatigue(updatedGame);
    }, [pitcherFatigue.checkPitcherFatigue, engine.justFinalized, engine.setRewardsAwarded, engine.setShowRewardsModal, setUserItems]);

    const handleRollClick = useCallback(async () => {
        await engine.handleRollDice({
            gameID,
            managerMode,
            onAfterReveal: handleAfterReveal,
        });
    }, [engine.handleRollDice, gameID, managerMode, handleAfterReveal]);

    const { handleAutoCycle } = useAutoManager({
        autoPlay, setAutoPlay, managerMode, setManagerMode,
        game: engine.game,
        participants: engine.participants,
        currentUser,
        rolling: engine.rolling,
        isMidEventModalOpen: engine.isMidEventModalOpen,
        isStealModalOpen: engine.isStealModalOpen,
        isModalOpen,
        isPitcherFatiguedModalOpen: pitcherFatigue.isPitcherFatiguedModalOpen,
        handleRollDice: handleRollClick,
        refreshGame: engine.refreshGame,
    });

    // ── 5. UI handlers ──
    const toggleManager = () => {
        setAutoPlay(false);
        setManagerMode(null);
        setIsManagerOpen(prev => !prev);
    };

    const subMenu = () => {
        setIsManagerOpen(false);
        setIsModalOpen(true);
    };

    const handleModalClose = async () => {
        setIsModalOpen(false);
        await engine.refreshGame();
    };

    // ── 6. Early returns ──
    if (engine.loading) return <LoadingSpinner />;
    if (engine.error) return <div className="error">{engine.error}</div>;

    // ── 7. Derived state ──
    const { game, participants } = engine;

    const normalizedSpecialType = game.specialGameType?.toLowerCase() || '';
    const userParticipant = participants?.find(p => String(p.userID) === String(currentUser.userID));
    const didUserWin = engine.rewardsAwarded?.didUserWin ?? false;
    const latestEvent = game.GameEvents?.[0] || {};
    const finalScore = {
        home: latestEvent?.homeScore ?? 0,
        away: latestEvent?.visitingScore ?? 0,
    };

    const stadium = game?.stadium;
    const useStadiumLayout = stadium?.visualConfig?.components && !normalizedSpecialType;

    const layoutProps = {
        game, participants, gameTheme, gameSpeed,
        engine, pitcherFatigue,
        autoPlay, managerMode,
        isManagerOpen, setIsManagerOpen,
        isParticipantsOpen, setIsParticipantsOpen,
        handleRollClick, toggleManager, subMenu, handleAutoCycle,
    };

    // ── 8. Render ──
    return (
        <>
            {game && (
                useStadiumLayout
                    ? <StadiumGameLayout {...layoutProps} stadium={stadium} />
                    : <DefaultGameLayout {...layoutProps} />
            )}
            {isModalOpen && (
                <Modal onClose={handleModalClose}>
                    <SubPlayerPage
                        participant={userParticipant}
                        latestEvent={game.GameEvents?.[0]}
                        onClose={handleModalClose}
                        pitcherWorkMap={pitcherFatigue.pitcherWorkMapRef.current}
                    />
                </Modal>
            )}
            {engine.isMidEventModalOpen && (
                <Modal onClose={engine.handleExtraBaseModalClose}>
                    <MidEventPage
                        participant={userParticipant}
                        latestEvent={game.GameEvents?.[0]}
                        onClose={engine.handleExtraBaseModalClose}
                    />
                </Modal>
            )}
            {engine.isStealModalOpen && (
                <Modal onClose={engine.handleStealModalClose}>
                    <StealEventPage
                        participant={userParticipant}
                        latestEvent={game.GameEvents?.[0]}
                        onClose={engine.handleStealModalClose}
                    />
                </Modal>
            )}
            {pitcherFatigue.isPitcherFatiguedModalOpen && (
                <Modal onClose={() => pitcherFatigue.setIsPitcherFatiguedModalOpen(false)}>
                    <div className="pitcher-fatigued-modal">
                        <div className="pitcher-fatigued-content">
                            <h3>Pitcher Out of Energy</h3>
                            <p>Your pitcher has reached their stamina limit and will pitch worse from here on out.</p>
                            <div className="pitcher-fatigued-buttons">
                                <button className="btn-primary" onClick={() => {
                                    pitcherFatigue.setIsPitcherFatiguedModalOpen(false);
                                    subMenu();
                                }}>
                                    Sub Pitcher
                                </button>
                                <button className="btn-danger" onClick={() => pitcherFatigue.setIsPitcherFatiguedModalOpen(false)}>
                                    Screw you, I know what I&apos;m doing
                                </button>
                            </div>
                        </div>
                        <img src="/img/pitcher-tired.gif" alt="Tired pitcher" className="pitcher-fatigued-img" />
                    </div>
                </Modal>
            )}
            {engine.showRewardsModal && (
                <Modal onClose={() => engine.setShowRewardsModal(false)} className="postgame-rewards-wrapper">
                    <PostGameRewardsModal
                        rewardsAwarded={engine.rewardsAwarded}
                        didUserWin={!!didUserWin}
                        finalScore={finalScore}
                        onClose={() => engine.setShowRewardsModal(false)}
                    />
                </Modal>
            )}
        </>
    );
};

export default PlayGamesPage;
